/**
 * Ceiling profiles — the shape of a room's ceiling, and the geometry that
 * falls out of it.
 *
 * A room's `height` is its WALL height: where the walls meet the ceiling at the
 * eaves. A profile rises above that:
 *
 *   flat   one level plane at the wall height (what every room had before)
 *   shed   one plane sloping from a low wall up to a high wall (`rise` higher)
 *   gable  two planes meeting at a ridge `rise` above the walls; the ridge can
 *          sit anywhere across the room (`ridgeAt` 0.5 = a cathedral ceiling,
 *          off-centre = an asymmetric gable with two different pitches)
 *
 * The RIDGE AXIS says which way the ridge (or a shed's high edge) runs: along
 * the room's length (x) or along its width (z). The slope then runs the other
 * way — "across". Across-coordinate `t` runs 0 … span; the `start` side is the
 * front wall (ridge along the length) or the left wall (ridge along the width).
 *
 * `ceilingGeometry(room)` is the one place every consumer asks: the planes
 * (each with a frame, so boards can be laid on it like a wall), the height at
 * any point, each wall's top edge as a function of position, and each wall's
 * outline polygon. Everything is pure and in feet.
 */

export type RidgeAxis = 'length' | 'width';
export type WallFaceName = 'front' | 'right' | 'back' | 'left';

export type CeilingProfile =
  | { kind: 'flat' }
  | { kind: 'shed'; ridgeAxis: RidgeAxis; highSide: 'start' | 'end'; rise: number }
  | { kind: 'gable'; ridgeAxis: RidgeAxis; ridgeAt: number; rise: number }
  /** Vaulted with a flat centre: two slopes up from the eaves to a level middle `flat` of the way across (0..1). */
  | { kind: 'vaulted'; ridgeAxis: RidgeAxis; flat: number; rise: number };

export const FLAT: CeilingProfile = { kind: 'flat' };

/** Standard roof pitches, rise per 12 of run, with plain words for them. */
export const STANDARD_PITCHES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12] as const;
export function pitchWord(pitch: number): 'gentle' | 'moderate' | 'steep' {
  if (pitch < 4.5) return 'gentle';
  if (pitch < 8.5) return 'moderate';
  return 'steep';
}
/** The rise for a pitch over a run; and back. */
export const riseForPitch = (pitch: number, run: number) => (pitch / 12) * run;
export const pitchForRise = (rise: number, run: number) => (run > 1e-9 ? (12 * rise) / run : 0);
/** The nearest standard pitch. */
export function snapPitch(pitch: number): number {
  return STANDARD_PITCHES.reduce((best, p) => (Math.abs(p - pitch) < Math.abs(best - pitch) ? p : best), STANDARD_PITCHES[0] as number);
}

export interface RoomBox {
  length: number;
  width: number;
  height: number;
  ceilingProfile?: CeilingProfile;
}

/** A point in room coordinates (x along the length, y up, z along the width). */
export type Vec3 = [number, number, number];

/**
 * One facet of the ceiling. Its surface coordinates are like a wall's: `x`
 * along `u` (the ridge direction), `y` along `v` (up the slope, from the eave
 * edge toward the ridge), the surface plane at local z = 0 and the room side
 * at +z. `matrix` (column-major, as THREE.Matrix4.fromArray takes it) maps a
 * surface point (x, y, z) to room coordinates.
 */
export interface CeilingPlane {
  id: string;
  /** Footprint in room coordinates. */
  x0: number; x1: number; z0: number; z1: number;
  /** Surface size: along the ridge, and up the slope (the true, sloped length). */
  width: number;
  height: number;
  /** Rise of the plane across its footprint (0 = flat), and its pitch in 12ths. */
  rise: number;
  pitch: number;
  origin: Vec3;
  u: Vec3;
  v: Vec3;
  /** Unit normal pointing INTO the room (down-ish). */
  normal: Vec3;
  matrix: number[];
  heightAt(x: number, z: number): number;
}

export interface CeilingGeometry {
  profile: CeilingProfile;
  eave: number;
  peak: number;
  planes: CeilingPlane[];
  heightAt(x: number, z: number): number;
  /** The wall's top edge, in that wall's own surface x (feet up from the floor). */
  wallTop(face: WallFaceName): (x: number) => number;
  /** The wall's outline, counter-clockwise from the bottom-left, in surface coordinates. */
  wallOutline(face: WallFaceName): [number, number][];
  /** The highest point of a wall (its surface rectangle's height). */
  wallMaxTop(face: WallFaceName): number;
  /** The ridge, in room coordinates, or null when flat. Two ends. */
  ridgeLine: [Vec3, Vec3] | null;
  /** Every line where the ceiling changes slope (a ridge, a shed's high edge, a vault's two shoulders), in room coordinates. */
  creases: [Vec3, Vec3][];
}

const EPS = 1e-9;
const norm = (v: Vec3): Vec3 => { const l = Math.hypot(...v) || 1; return [v[0] / l, v[1] / l, v[2] / l]; };
const cross = (a: Vec3, b: Vec3): Vec3 => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const scale = (v: Vec3, s: number): Vec3 => [v[0] * s, v[1] * s, v[2] * s];
const add = (a: Vec3, b: Vec3): Vec3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];

/** Room length × width spans: along the ridge, and across the slope. */
function spans(room: RoomBox, axis: RidgeAxis): { along: number; across: number } {
  return axis === 'length' ? { along: room.length, across: room.width } : { along: room.width, across: room.length };
}

/** A room point from (along, across) in the ridge's frame. */
function roomPoint(axis: RidgeAxis, along: number, across: number, y: number): Vec3 {
  return axis === 'length' ? [along, y, across] : [across, y, along];
}

/**
 * The surface x of a wall, from a room point on that wall (walls are seen from
 * inside: front and left read left-to-right as +x / +z; back and right the other way).
 */
function wallSurfaceX(room: RoomBox, face: WallFaceName, x: number, z: number): number {
  switch (face) {
    case 'front': return x;
    case 'back': return room.length - x;
    case 'left': return z;
    case 'right': return room.width - z;
  }
}
/** The other way: a wall's surface x to the room point at its base. */
function wallRoomPoint(room: RoomBox, face: WallFaceName, sx: number): [number, number] {
  switch (face) {
    case 'front': return [sx, 0];
    case 'back': return [room.length - sx, room.width];
    case 'left': return [0, sx];
    case 'right': return [room.length, room.width - sx];
  }
}
const wallLength = (room: RoomBox, face: WallFaceName) => (face === 'front' || face === 'back' ? room.length : room.width);

/**
 * One sloped (or flat) plane from an eave edge at across = `tFrom` up to
 * across = `tTo`, rising `rise`. The surface frame: u along the ridge, v up the
 * slope; `u` is turned so the inward normal (u × v) points down into the room.
 */
function makePlane(room: RoomBox, axis: RidgeAxis, id: string, tFrom: number, tTo: number, rise: number, eave: number): CeilingPlane {
  const { along } = spans(room, axis);
  const run = Math.abs(tTo - tFrom);
  const slope = Math.hypot(run, rise);
  const dir = Math.sign(tTo - tFrom) || 1;
  // v: up the slope. u: along the ridge, chosen so u × v points downward.
  const v = norm(roomPoint(axis, 0, dir * run, rise));
  let u = norm(roomPoint(axis, 1, 0, 0));
  let n = cross(u, v);
  let originAlong = 0;
  if (n[1] > 0) { u = scale(u, -1); n = cross(u, v); originAlong = along; }
  const origin = roomPoint(axis, originAlong, tFrom, eave);
  const lo = Math.min(tFrom, tTo), hi = Math.max(tFrom, tTo);
  const foot = axis === 'length' ? { x0: 0, x1: room.length, z0: lo, z1: hi } : { x0: lo, x1: hi, z0: 0, z1: room.width };
  const heightAt = (x: number, z: number) => {
    const t = axis === 'length' ? z : x;
    return run < EPS ? eave : eave + rise * Math.max(0, Math.min(1, (t - tFrom) / (tTo - tFrom)));
  };
  // Column-major 4×4: columns u, v, n, origin.
  const matrix = [u[0], u[1], u[2], 0, v[0], v[1], v[2], 0, n[0], n[1], n[2], 0, origin[0], origin[1], origin[2], 1];
  return { id, ...foot, width: along, height: slope, rise, pitch: pitchForRise(rise, run), origin, u, v, normal: n, matrix, heightAt };
}

/** The flat plane: surface x along the length, y along the width, as the old ceiling. */
function flatPlane(room: RoomBox): CeilingPlane {
  const h = room.height;
  // u = +x, v = +z, normal = u × v = (0,-1,0): down. Origin at the front-left corner.
  return {
    id: 'ceiling', x0: 0, x1: room.length, z0: 0, z1: room.width,
    width: room.length, height: room.width, rise: 0, pitch: 0,
    origin: [0, h, 0], u: [1, 0, 0], v: [0, 0, 1], normal: [0, -1, 0],
    matrix: [1, 0, 0, 0, 0, 0, 1, 0, 0, -1, 0, 0, 0, h, 0, 1],
    heightAt: () => h,
  };
}

/** Is the profile sound for this room? (Null when it is; the reason when not.) */
export function profileIssue(room: RoomBox, profile: CeilingProfile, maxHeight = 24): string | null {
  if (profile.kind === 'flat') return null;
  if (!(profile.rise > 0)) return 'rise';
  if (room.height + profile.rise > maxHeight + EPS) return 'too-tall';
  if (profile.kind === 'gable' && (profile.ridgeAt <= 0 || profile.ridgeAt >= 1)) return 'ridge';
  if (profile.kind === 'vaulted' && (profile.flat <= 0 || profile.flat >= 1)) return 'flat';
  return null;
}

export function ceilingGeometry(room: RoomBox): CeilingGeometry {
  const profile = room.ceilingProfile ?? FLAT;
  const eave = room.height;
  let planes: CeilingPlane[];
  let ridgeLine: [Vec3, Vec3] | null = null;
  const creases: [Vec3, Vec3][] = [];
  let peak = eave;

  if (profile.kind === 'flat' || profileIssue(room, profile) !== null) {
    planes = [flatPlane(room)];
  } else if (profile.kind === 'shed') {
    const { along, across } = spans(room, profile.ridgeAxis);
    peak = eave + profile.rise;
    planes = profile.highSide === 'end'
      ? [makePlane(room, profile.ridgeAxis, 'ceiling-slope', 0, across, profile.rise, eave)]
      : [makePlane(room, profile.ridgeAxis, 'ceiling-slope', across, 0, profile.rise, eave)];
    const tHigh = profile.highSide === 'end' ? across : 0;
    ridgeLine = [roomPoint(profile.ridgeAxis, 0, tHigh, peak), roomPoint(profile.ridgeAxis, along, tHigh, peak)];
    creases.push(ridgeLine);
  } else if (profile.kind === 'vaulted') {
    // Two slopes to a level centre: the flat part is `flat` of the span, centred.
    const { along, across } = spans(room, profile.ridgeAxis);
    const run = (across * (1 - profile.flat)) / 2;
    peak = eave + profile.rise;
    planes = [
      makePlane(room, profile.ridgeAxis, 'ceiling-start', 0, run, profile.rise, eave),
      makePlane(room, profile.ridgeAxis, 'ceiling-flat', run, across - run, 0, peak),
      makePlane(room, profile.ridgeAxis, 'ceiling-end', across, across - run, profile.rise, eave),
    ];
    for (const t of [run, across - run]) creases.push([roomPoint(profile.ridgeAxis, 0, t, peak), roomPoint(profile.ridgeAxis, along, t, peak)]);
  } else {
    const { along, across } = spans(room, profile.ridgeAxis);
    const r = profile.ridgeAt * across;
    peak = eave + profile.rise;
    planes = [
      makePlane(room, profile.ridgeAxis, 'ceiling-start', 0, r, profile.rise, eave),
      makePlane(room, profile.ridgeAxis, 'ceiling-end', across, r, profile.rise, eave),
    ];
    ridgeLine = [roomPoint(profile.ridgeAxis, 0, r, peak), roomPoint(profile.ridgeAxis, along, r, peak)];
    creases.push(ridgeLine);
  }

  const heightAt = (x: number, z: number): number => {
    const plane = planes.find((p) => x >= p.x0 - EPS && x <= p.x1 + EPS && z >= p.z0 - EPS && z <= p.z1 + EPS) ?? planes[0];
    return plane.heightAt(x, z);
  };
  const wallTop = (face: WallFaceName) => (sx: number) => {
    const [x, z] = wallRoomPoint(room, face, Math.max(0, Math.min(wallLength(room, face), sx)));
    return heightAt(x, z);
  };
  const wallOutline = (face: WallFaceName): [number, number][] => {
    const w = wallLength(room, face);
    const top = wallTop(face);
    // Breakpoints: the wall's ends, and wherever a plane boundary crosses it.
    const xs = new Set<number>([0, w]);
    for (const p of planes) {
      for (const [x, z] of [[p.x0, p.z0], [p.x1, p.z0], [p.x0, p.z1], [p.x1, p.z1]] as [number, number][]) {
        const onWall = (face === 'front' && Math.abs(z) < EPS) || (face === 'back' && Math.abs(z - room.width) < EPS)
          || (face === 'left' && Math.abs(x) < EPS) || (face === 'right' && Math.abs(x - room.length) < EPS);
        if (onWall) { const sx = wallSurfaceX(room, face, x, z); if (sx > EPS && sx < w - EPS) xs.add(sx); }
      }
    }
    const sorted = [...xs].sort((a, b) => a - b);
    const topEdge = sorted.map((sx) => [sx, top(sx)] as [number, number]).reverse();
    // Drop collinear middle points (a plain rectangle stays four points).
    const cleaned = topEdge.filter((p, i) => i === 0 || i === topEdge.length - 1 || Math.abs(p[1] - topEdge[i - 1][1]) > EPS || Math.abs(p[1] - topEdge[i + 1][1]) > EPS);
    return [[0, 0], [w, 0], ...cleaned];
  };
  const wallMaxTop = (face: WallFaceName) => Math.max(...wallOutline(face).map((p) => p[1]));

  return { profile, eave, peak, planes, heightAt, wallTop, wallOutline, wallMaxTop, ridgeLine, creases };
}

/** Plain-language summary pieces for the UI. */
export function describeProfile(room: RoomBox): { pitches: number[]; peak: number; eave: number } {
  const g = ceilingGeometry(room);
  return { pitches: [...new Set(g.planes.filter((p) => p.rise > EPS).map((p) => Math.round(p.pitch * 10) / 10))], peak: g.peak, eave: g.eave };
}
